/**
 * uploadJobDocuments
 *
 * Shared helper for the "upload supporting documents alongside a work-order
 * action" flow (reject, request-more-info, reply). Each file is:
 *   1. presigned via POST /jobs/documents/:jobId/upload-url
 *   2. uploaded directly to S3 (multipart POST)
 *   3. registered via POST /jobs/documents/:jobId/complete → creates a job_file row
 *
 * The persisted job_file rows are what make the documents visible in Lite (the
 * documents tab reads them). The returned `attachments` array is what the
 * directional handlers forward to CORE on the outbound event, which is what
 * makes the documents visible in CORE. Callers need both, so we return both.
 */

import {
  completeJobDocumentUpload,
  getJobDocumentUploadUrl,
} from '@/domains/jobs/api/jobDocumentsApi';
import { uploadToS3Presigned } from '@/shared/lib/presignedUpload';
import type { OutboundRejectedBody } from '@skewb-works/contracts';

/** Attachment shape the outbound reject/more-info payloads expect (per job). */
export type OutboundAttachment = NonNullable<
  OutboundRejectedBody['rejections'][number]['attachments']
>[number];

/**
 * Upload every file for a single job and return the attachment references to
 * ship on the outbound event. `S3BucketName` is intentionally left blank — the
 * backend overwrites it with the document bucket the file was actually uploaded
 * to (resolveDocumentBucket → S3_DOCUMENT_BUCKET) before forwarding to CORE.
 */
export async function uploadJobDocuments(
  jobId: number,
  files: File[],
): Promise<OutboundAttachment[]> {
  const attachments: OutboundAttachment[] = [];

  for (const file of files) {
    const contentType = file.type || 'application/octet-stream';

    const presigned = await getJobDocumentUploadUrl(jobId, {
      filename: file.name,
      content_type: contentType,
    });

    await uploadToS3Presigned(presigned, file);

    const doc = await completeJobDocumentUpload(jobId, {
      name: file.name,
      mime_type: contentType,
      object_key: presigned.object_key,
    });

    if (doc.s3_path) {
      attachments.push({
        fileName: doc.name ?? file.name,
        mimeType: doc.mime_type ?? undefined,
        date: doc.created_time ?? undefined,
        S3BucketName: '', // backend overwrites with the document bucket (resolveDocumentBucket) before forwarding to CORE
        S3Key: doc.s3_path,
      });
    }
  }

  return attachments;
}
